import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  mockTradingRulesViolations,
  getMockMetricsByTimeRange,
  getMockScenario,
  getMockBreachesForScenario,
  getMockViolationsForScenario,
  getMockWarningTodayScenarioPatch
} from '../mock/tradingRulesMetricsMock';
import useAPI from './useApi';
import { resolveTradingRulesMockScenario } from '../utils/tradingRulesMockScenario';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to fetch trading rules metrics and violations
 * 
 * Uses mock data when v= query parameter is present, otherwise uses real API.
 * Calls /trading_rules_metrics/summary, /trading_rules_violations, and /trading_rules_breaches
 * 
 * @param {Object} account - Selected account object
 * @param {string} timeRange - 'today', '7days', or '30days'
 * @returns {Object} { metrics, violations, breaches, loading, error, refetch }
 */
const useTradingRulesMetrics = (account, timeRange) => {
  const [metrics, setMetrics] = useState(null);
  const [violations, setViolations] = useState([]);
  const [violationsByType, setViolationsByType] = useState({});
  const [breaches, setBreaches] = useState([]);
  const [journalCompliance, setJournalCompliance] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isInitialLoadRef = useRef(true);
  const requestIdRef = useRef(0);
  const { request } = useAPI();
  // Flip loading in the same render as a timeframe change so the chart can
  // show a spinner before the effect fires, without a stale series flash.
  const [trackedRange, setTrackedRange] = useState(timeRange);
  if (trackedRange !== timeRange) {
    setTrackedRange(timeRange);
    const nextLoading = Boolean(account?.AccountID && timeRange);
    if (loading !== nextLoading) setLoading(nextLoading);
  }

  const fetchMetrics = useCallback(async (isRefresh = false) => {
    const requestId = ++requestIdRef.current;
    const stillCurrent = () => requestId === requestIdRef.current;

    // Don't fetch if no account or invalid timeRange
    if (!account?.AccountID || timeRange === null || timeRange === undefined || timeRange === '') {
      if (stillCurrent()) setLoading(false);
      return;
    }
      // Show the chart spinner on a timeframe change, not on a background refresh
      if (!isRefresh && isInitialLoadRef.current) {
        setLoading(true);
      }
      setError(null);

      try {
        const { useMock, scenarioVersion } = resolveTradingRulesMockScenario();

        if (useMock) {

          let mockMetrics;
          let violationsByTypeData = {};

          if (scenarioVersion) {
            const scenario = getMockScenario(scenarioVersion);
            const isWarning =
              scenarioVersion === '2' || scenarioVersion === 'warning';
            if (isWarning && timeRange === 'today') {
              mockMetrics = getMockWarningTodayScenarioPatch(scenario);
              violationsByTypeData = mockMetrics.violations_by_type || {};
            } else {
              mockMetrics = scenario;
              violationsByTypeData = scenario.violations_by_type || {};
            }
          } else {
            // Use default mock data
            mockMetrics = getMockMetricsByTimeRange(timeRange);
            violationsByTypeData = mockTradingRulesViolations.violations_by_type || {};
          }

          setMetrics(mockMetrics);
          setViolations(getMockViolationsForScenario(scenarioVersion, timeRange));
          setViolationsByType(violationsByTypeData);
          // Breaches: use scenario-specific breaches
          // Mock breaches are already filtered by timeRange in LossLimitHistoryTimeline component
          setBreaches(getMockBreachesForScenario(scenarioVersion));
          // Journal compliance data (respects timeRange)
          setJournalCompliance(mockMetrics.journal_compliance || {});

          console.log('[useTradingRulesMetrics] Using mock data');
          if (!stillCurrent()) return;
        } else {
          // Real API mode: Make parallel requests to all endpoints
          const accountId = account.AccountID;
          
          console.log(`[useTradingRulesMetrics] Fetching from API: account=${accountId}, timeRange=${timeRange}`);
          
          const [metricsResponse, violationsResponse, breachesResponse] = await Promise.all([
            request(
              `${BACKEND_URL}/trading_rules_metrics/summary?account_id=${accountId}&time_range=${timeRange}`,
              {},
              'GET'
            ),
            request(
              `${BACKEND_URL}/trading_rules_violations?account_id=${accountId}&time_range=${timeRange}`,
              {},
              'GET'
            ),
            request(
              `${BACKEND_URL}/trading_rules_breaches?account_id=${accountId}&time_range=${timeRange}`,
              {},
              'GET'
            )
          ]);

          // Set metrics from summary endpoint
          // Handle response structure: { metrics: {...}, journal_compliance: {...} } or just { metrics: {...} }
          // Also handle if response has date/account_id at top level: { date, account_id, metrics: {...} }
          // The API returns: { date, account_id, metrics: {...}, journal_compliance: {...} }
          // Extract the metrics object from the response
          if (!stillCurrent()) return;

          const metricsData = metricsResponse?.metrics || metricsResponse;
          setMetrics(metricsData);
          setJournalCompliance(metricsResponse?.journal_compliance || {});

          // Set violations from violations endpoint
          setViolations(violationsResponse?.violations || []);
          setViolationsByType(violationsResponse?.violations_by_type || {});

          // Set breaches from breaches endpoint
          setBreaches(breachesResponse || []);

          console.log('[useTradingRulesMetrics] Fetched real data from API');
        }

        if (!stillCurrent()) return;
        setLoading(false);
        isInitialLoadRef.current = false;
      } catch (err) {
        if (!stillCurrent()) return;
        console.error('[useTradingRulesMetrics] Error fetching metrics:', err);
        setError(err.message || 'Failed to load metrics');
        setLoading(false);
        isInitialLoadRef.current = false;
      }
  }, [account?.AccountID, timeRange, request]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    isInitialLoadRef.current = true;
    fetchMetrics(false);
  }, [account?.AccountID, timeRange, fetchMetrics]);

  // Refetch function that doesn't show loading spinner
  const refetch = useCallback(() => {
    fetchMetrics(true);
  }, [fetchMetrics]);

  useEffect(() => {
    const handleViolationDeleted = () => {
      refetch();
    };
    window.addEventListener('tradingRules:violationDeleted', handleViolationDeleted);
    return () => window.removeEventListener('tradingRules:violationDeleted', handleViolationDeleted);
  }, [refetch]);

  return {
    metrics,
    violations,
    violationsByType,
    breaches,
    journalCompliance,
    loading,
    error,
    refetch
  };
};

export default useTradingRulesMetrics;
